import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  Menu as MenuIcon,
  ChevronLeft,
} from '@mui/icons-material'
import { useAuthStore } from '../../stores/auth.store'
import { useUIStore } from '../../stores/ui.store'
import { useToastStore } from '../../stores/toast.store'
import { logout as apiLogout, CRMObjectRow } from '../../api/client'
import { useApi } from '../../api/client'
import { ObjectSidebar } from './ObjectSidebar'
import { HeaderUserMenu } from './HeaderUserMenu'
import { ChangePasswordDialog } from './ChangePasswordDialog'
import TabBar from './TabBar'

export function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, token, clearAuth } = useAuthStore()
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore()
  const { toasts, removeToast, error, success } = useToastStore()

  const [changePwOpen, setChangePwOpen] = useState(false)

  const { data: objectsData, isLoading: objectsLoading } = useApi<{ data: CRMObjectRow[] }>(
    token ? '/objects' : null,
  )
  const objects = objectsData?.data ?? []

  useEffect(() => {
    setSidebarOpen(true)
  }, [setSidebarOpen])

  useEffect(() => {
    const handleForbidden = () => {
      error('您没有权限执行此操作')
    }
    const handleServerError = () => {
      error('服务器错误，请稍后重试')
    }

    window.addEventListener('crm:forbidden', handleForbidden)
    window.addEventListener('crm:server-error', handleServerError)
    return () => {
      window.removeEventListener('crm:forbidden', handleForbidden)
      window.removeEventListener('crm:server-error', handleServerError)
    }
  }, [error])

  const handleLogout = async () => {
    try {
      await apiLogout()
    } catch {
      // ignore
    }
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: 1,
        }}
      >
        <Toolbar variant="dense">
          <IconButton edge="start" onClick={toggleSidebar} sx={{ mr: 1 }}>
            {sidebarOpen ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700 }}>
            {user?.tenant_name || 'CRM'}
          </Typography>
          <HeaderUserMenu
            user={user}
            onChangePassword={() => setChangePwOpen(true)}
            onLogout={handleLogout}
          />
        </Toolbar>
      </AppBar>

      <ObjectSidebar
        open={sidebarOpen}
        pathname={location.pathname}
        objects={objects}
        loading={objectsLoading}
        onNavigate={navigate}
      />

      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Toolbar variant="dense" />
        <TabBar />
        <Box sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default', minHeight: 0, overflow: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>

      <ChangePasswordDialog
        open={changePwOpen}
        onClose={() => setChangePwOpen(false)}
        onSuccess={() => success('密码修改成功')}
      />

      {toasts.map((toast) => (
        <Snackbar
          key={toast.id}
          open
          autoHideDuration={4000}
          onClose={() => removeToast(toast.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ position: 'fixed', bottom: toasts.indexOf(toast) * 60 + 24 }}
        >
          <Alert
            onClose={() => removeToast(toast.id)}
            severity={toast.severity}
            variant="filled"
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  )
}
